import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Building2, Camera, BarChart3,
  LogOut, Activity, History, Receipt
} from "lucide-react";

const NAV = {
  super_admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/clients", label: "Clients", icon: Building2 },
    { to: "/admin/screenshots", label: "Screenshots", icon: Camera },
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
    { to: "/admin/invoices", label: "Invoices", icon: Receipt },
  ],
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/clients", label: "Clients", icon: Building2 },
    { to: "/admin/screenshots", label: "Screenshots", icon: Camera },
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  ],
  client: [
    { to: "/client", label: "Overview", icon: LayoutDashboard },
    { to: "/client/screenshots", label: "Screenshots", icon: Camera },
    { to: "/client/reports", label: "Reports", icon: BarChart3 },
    { to: "/client/invoices", label: "Invoices", icon: Receipt },
  ],
  employee: [
    { to: "/track", label: "Tracker", icon: Activity },
    { to: "/history", label: "History", icon: History },
  ],
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const nav = NAV[user?.role] || [];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const roleLabel = {
    super_admin: "Super Admin",
    admin: "Admin",
    client: "Client",
    employee: "Employee",
  }[user?.role] || "User";

  return (
    <div className="min-h-screen flex bg-[#fdfcf7]">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-stone-200 bg-white">
        <div className="px-5 py-5 border-b border-stone-200">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/taskmaster-logo.jpg"
              alt="Taskmaster Philippines"
              className="h-12 w-12 rounded-md object-cover ring-1 ring-stone-200"
            />
            <div className="min-w-0">
              <div className="font-display text-[17px] font-semibold leading-tight tracking-tight text-[#1f4d2e]">
                Taskmaster Philippines
              </div>
              <div className="font-display-italic text-[11px] text-stone-500 mt-0.5 leading-tight">
                Your Virtual Assistance, Your Virtual services
              </div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => {
            const Icon = n.icon;
            const active = location.pathname === n.to || (n.to !== "/admin" && location.pathname.startsWith(n.to));
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}-link`}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-elegant border-l-2 rounded-r-md ${
                  active
                    ? "bg-[#1f4d2e]/[0.06] border-[#d4a82a] text-[#1f4d2e] font-semibold"
                    : "border-transparent text-stone-700 hover:bg-stone-50"
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{n.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-stone-200 space-y-1">
          <div className="px-3 py-2">
            <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-bold">
              {roleLabel}
            </div>
            <div className="text-sm font-bold truncate">{user?.name}</div>
            <div className="text-xs text-stone-500 truncate font-mono-num">{user?.email}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-stone-200 flex items-center justify-between px-4 h-14">
        <Link to="/" className="flex items-center gap-2 min-w-0">
          <img src="/taskmaster-logo.jpg" alt="Taskmaster Philippines" className="h-9 w-9 rounded-md object-cover ring-1 ring-stone-200" />
          <div className="min-w-0">
            <div className="font-display text-[15px] font-semibold tracking-tight text-[#1f4d2e] leading-none truncate">
              Taskmaster Philippines
            </div>
            <div className="font-display-italic text-[10px] text-stone-500 leading-tight truncate">
              Your Virtual Assistance, Your Virtual services
            </div>
          </div>
        </Link>
        <button
          data-testid="mobile-logout-button"
          onClick={handleLogout}
          className="text-xs tracking-[0.2em] uppercase font-bold text-stone-700"
        >
          Sign Out
        </button>
      </div>

      {/* Mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-stone-200 flex">
        {nav.map((n) => {
          const Icon = n.icon;
          const active = location.pathname === n.to || (n.to !== "/admin" && location.pathname.startsWith(n.to));
          return (
            <Link
              key={n.to}
              to={n.to}
              className={`flex-1 flex flex-col items-center justify-center py-2 text-[10px] tracking-wider uppercase ${
                active ? "text-[#1f4d2e] font-bold" : "text-stone-500"
              }`}
            >
              <Icon className="h-4 w-4 mb-1" />
              {n.label}
            </Link>
          );
        })}
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0 pb-16 md:pb-0">
        {children}
      </main>
    </div>
  );
}
